import {
  EXTERNAL_LINKS,
  PRIVATE_ROUTES,
  REDIRECT_TO_URL_PATH_REGEX,
  ROUTES,
} from './constants'

/** Whether `redirectUrl` points at the Play app (same origin as `playBaseUrl`). */
function isRedirectTargetPlayApp(
  redirectUrl: string,
  playBaseUrl: string,
  pageOrigin: string
): boolean {
  try {
    const playOrigin = new URL(playBaseUrl).origin
    const targetOrigin = new URL(redirectUrl, pageOrigin).origin
    return targetOrigin === playOrigin
  } catch {
    return false
  }
}

function redirectToAuthPage(path: string, redirectTo = '/') {
  if (
    window.location.pathname.includes(ROUTES.logout) ||
    window.location.pathname.includes(ROUTES.login)
  ) {
    return
  }

  const finalUrl = new URL(path, window.location.href)

  if (isValidRedirect(redirectTo)) {
    finalUrl.searchParams.append('redirectTo', redirectTo)
  }

  window.location.href = finalUrl.toString()
}

export function logout(redirectTo = '/') {
  return redirectToAuthPage(ROUTES.logout, redirectTo)
}

export function redirectToLogin(redirectTo = '/') {
  return redirectToAuthPage(ROUTES.login, redirectTo)
}

export function isPrivateRoute(route: string) {
  return PRIVATE_ROUTES.some((privateRoute: string | RegExp) => {
    if (privateRoute instanceof RegExp) {
      return privateRoute.exec(route)
    }

    return privateRoute === route
  })
}

export function isValidRedirect(path: string) {
  const isSafeExternalLink = Object.values(EXTERNAL_LINKS).includes(path)

  const isPath = REDIRECT_TO_URL_PATH_REGEX.test(path)
  const isValidRoute = ![
    ROUTES.login,
    ROUTES.authLogin,
    ROUTES.signup,
    ROUTES.accessDenied,
    ROUTES.notFound,
  ].includes(path)

  const isMydraDomain =
    path && /^https:\/\/(?:[\w-]+\.)*mydra\.io(?:\/.*)?$/.test(path)

  // allow localhost URLs for local development
  const isLocalhost =
    path && /^http:\/\/localhost(?::\d+)?(?:\/.*)?$/.test(path)

  return !!(
    path &&
    (isMydraDomain ||
      isLocalhost ||
      isSafeExternalLink ||
      (isPath && isValidRoute))
  )
}

/**
 * Ai-business: Play base if no safe redirect; if redirect targets Play, use it (e.g. deep links); otherwise Play base.
 */
export function resolveAiBusinessRedirectDestination(
  playUrl: string,
  redirectCandidate: string | undefined,
  pageOrigin: string
): string {
  if (
    redirectCandidate &&
    isValidRedirect(redirectCandidate) &&
    isRedirectTargetPlayApp(redirectCandidate, playUrl, pageOrigin)
  ) {
    return new URL(redirectCandidate, pageOrigin).href
  }
  return playUrl
}
